import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderKanban, FilePlus2, Send, Search as SearchIcon, CheckCircle2, Rocket,
  Flag, Clock, PauseCircle, TrendingUp, RefreshCcw, ArrowRight,
  Users, FileText, Receipt, Calendar, Upload, GitBranch, History as HistoryIcon,
  ListChecks, Plus,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { HistoryList } from "./ProcessHistoryComponents";

// -- Stage → JMS-tone mapping (reuses existing badge tones, no new colors)
const STAGE_ICONS = {
  cadastro:   { icon: FilePlus2,    tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  enviado:    { icon: Send,         tone: "bg-sky-50 text-sky-700 border-sky-200" },
  analise:    { icon: SearchIcon,   tone: "bg-amber-50 text-amber-700 border-amber-200" },
  aprovado:   { icon: CheckCircle2, tone: "bg-lime-50 text-lime-700 border-lime-200" },
  publicado:  { icon: Rocket,       tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  finalizado: { icon: CheckCircle2, tone: "bg-primary/10 text-primary border-primary/30" },
};

const STAGE_LABELS = {
  cadastro: "Cadastro", enviado: "Enviado", analise: "Análise",
  aprovado: "Aprovado", publicado: "Publicado", finalizado: "Finalizado",
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d atrás`;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};

// -- Small building blocks (all reuse existing card / border / spacing tokens)

function KpiCard({ testid, label, value, icon: Icon, tone, percentage, href }) {
  const Wrapper = href ? Link : "div";
  return (
    <Wrapper
      to={href}
      data-testid={testid}
      className={`rounded-2xl border border-border bg-white p-4 flex items-start gap-3 transition ${
        href ? "hover:border-primary/40 hover:shadow-sm" : ""
      }`}
    >
      <div className={`h-11 w-11 shrink-0 rounded-lg border grid place-items-center ${tone || "bg-primary/10 text-primary border-primary/30"}`}>
        <Icon className="h-5 w-5" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
        <div className="flex items-baseline gap-2 mt-0.5">
          <p className="text-2xl font-semibold tabular-nums text-foreground">{value ?? 0}</p>
          {percentage != null && (
            <span className="text-[11px] text-muted-foreground">{percentage}%</span>
          )}
        </div>
      </div>
    </Wrapper>
  );
}

function ProgressBar({ value, tone = "bg-primary" }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
      <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
    </div>
  );
}

function SectionCard({ title, subtitle, action, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// -- Main page

export default function ProcessesDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/processes/dashboard/summary");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stageStyle = (key) => STAGE_ICONS[key] || {
    icon: FolderKanban, tone: "bg-slate-50 text-slate-700 border-slate-200"
  };

  const totalProcesses = data?.totals?.processes ?? 0;
  const workflowStages = data?.workflow_stages || [];
  const statusBuckets = data?.status_buckets || { em_andamento: 0, on_hold: 0, finalizado: 0 };
  const priority = data?.priority || { high: 0, medium: 0, low: 0 };
  const avgProgress = data?.average_progress ?? 0;
  const latestProcesses = data?.latest_processes || [];
  const latestHistory = data?.latest_history || [];
  const totals = data?.totals || {};
  const timeline = data?.timeline || { today: 0, this_week: 0 };
  const topResponsible = data?.top_responsible || [];

  return (
    <div data-testid="processes-page-dashboard" className="space-y-6">
      {/* Refresh + Quick Actions header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="dashboard-refresh">
            <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-quick-actions">
          <Link to="/processes/list?new=1">
            <Button size="sm" data-testid="qa-new-process"><Plus className="h-4 w-4 mr-1" /> Novo Processo</Button>
          </Link>
          <Link to="/processes/list">
            <Button variant="outline" size="sm" data-testid="qa-list-processes"><ListChecks className="h-4 w-4 mr-1" /> Lista de Processos</Button>
          </Link>
          <Link to="/processes/documents">
            <Button variant="outline" size="sm" data-testid="qa-upload-doc"><Upload className="h-4 w-4 mr-1" /> Upload Documento</Button>
          </Link>
          <Link to="/processes/timeline">
            <Button variant="outline" size="sm" data-testid="qa-timeline"><GitBranch className="h-4 w-4 mr-1" /> Timeline</Button>
          </Link>
          <Link to="/processes/history">
            <Button variant="outline" size="sm" data-testid="qa-history"><HistoryIcon className="h-4 w-4 mr-1" /> Histórico</Button>
          </Link>
        </div>
      </div>

      {/* KPI cards: Total + one per stage */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="dashboard-kpis">
        <KpiCard
          testid="kpi-total"
          label="Total de Processos"
          value={totalProcesses}
          icon={FolderKanban}
          tone="bg-primary/10 text-primary border-primary/30"
          href="/processes/list"
        />
        {workflowStages.map((s) => {
          const st = stageStyle(s.key);
          return (
            <KpiCard
              key={s.key}
              testid={`kpi-stage-${s.key}`}
              label={STAGE_LABELS[s.key] || s.label}
              value={s.count}
              icon={st.icon}
              tone={st.tone}
              percentage={s.percentage}
              href={`/processes/list?workflow_stage=${s.key}`}
            />
          );
        })}
      </div>

      {/* Row 1: Workflow summary + Status buckets + Priority */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard
          testid="section-workflow"
          title="Resumo de Workflow"
          subtitle={loading ? "Carregando…" : `${totalProcesses} processo${totalProcesses === 1 ? "" : "s"}`}
        >
          <ul className="space-y-3">
            {workflowStages.map((s) => {
              const st = stageStyle(s.key);
              const Icon = st.icon;
              return (
                <li key={s.key} data-testid={`workflow-row-${s.key}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`h-6 w-6 rounded-md border grid place-items-center ${st.tone}`}>
                      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                    </span>
                    <span className="text-sm font-medium text-foreground flex-1">{STAGE_LABELS[s.key] || s.label}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">{s.count} · {s.percentage}%</span>
                  </div>
                  <ProgressBar value={s.percentage} />
                </li>
              );
            })}
          </ul>
        </SectionCard>

        <SectionCard testid="section-status" title="Situação dos Processos" subtitle="Status agrupados">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl border border-border p-3 text-center" data-testid="status-em-andamento">
              <div className="h-10 w-10 rounded-lg mx-auto grid place-items-center bg-sky-50 text-sky-700 border border-sky-200">
                <Clock className="h-5 w-5" />
              </div>
              <p className="mt-1 text-lg font-semibold tabular-nums">{statusBuckets.em_andamento}</p>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Em andamento</p>
            </div>
            <div className="rounded-xl border border-border p-3 text-center" data-testid="status-on-hold">
              <div className="h-10 w-10 rounded-lg mx-auto grid place-items-center bg-amber-50 text-amber-700 border border-amber-200">
                <PauseCircle className="h-5 w-5" />
              </div>
              <p className="mt-1 text-lg font-semibold tabular-nums">{statusBuckets.on_hold}</p>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">On Hold</p>
            </div>
            <div className="rounded-xl border border-border p-3 text-center" data-testid="status-finalizado">
              <div className="h-10 w-10 rounded-lg mx-auto grid place-items-center bg-primary/10 text-primary border border-primary/30">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <p className="mt-1 text-lg font-semibold tabular-nums">{statusBuckets.finalizado}</p>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Finalizado</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Progresso médio</p>
              <p className="text-sm font-semibold tabular-nums" data-testid="dashboard-avg-progress">{avgProgress}%</p>
            </div>
            <ProgressBar value={avgProgress} />
          </div>
        </SectionCard>

        <SectionCard testid="section-priority" title="Prioridade dos Processos">
          <ul className="space-y-3">
            <PriorityRow testid="priority-high" label="Alta"  count={priority.high}   total={totalProcesses} tone="bg-rose-50 text-rose-700 border-rose-200" barTone="bg-rose-500" icon={Flag} />
            <PriorityRow testid="priority-medium" label="Média" count={priority.medium} total={totalProcesses} tone="bg-amber-50 text-amber-700 border-amber-200" barTone="bg-amber-500" icon={Flag} />
            <PriorityRow testid="priority-low"  label="Baixa" count={priority.low}    total={totalProcesses} tone="bg-slate-50 text-slate-700 border-slate-200" barTone="bg-slate-400" icon={Flag} />
          </ul>
        </SectionCard>
      </div>

      {/* Row 2: Documents summary + Timeline summary + Responsible users */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard testid="section-documents" title="Resumo de Documentos">
          <div className="grid grid-cols-3 gap-2">
            <MiniStat testid="docs-total" icon={FileText} label="Documentos" value={totals.documents} tone="bg-slate-50 text-slate-700 border-slate-200" />
            <MiniStat testid="docs-receipts" icon={Receipt} label="Recibos" value={totals.receipts} tone="bg-emerald-50 text-emerald-700 border-emerald-200" />
            <MiniStat testid="docs-today" icon={Upload} label="Hoje" value={totals.documents_today} tone="bg-sky-50 text-sky-700 border-sky-200" />
          </div>
          <div className="mt-4 flex justify-end">
            <Link to="/processes/documents" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="link-documents">
              Ver documentos <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </SectionCard>

        <SectionCard testid="section-timeline" title="Resumo da Timeline">
          <div className="grid grid-cols-2 gap-2">
            <MiniStat testid="tl-today" icon={Calendar} label="Eventos hoje" value={timeline.today} tone="bg-sky-50 text-sky-700 border-sky-200" />
            <MiniStat testid="tl-week" icon={Calendar} label="Nesta semana" value={timeline.this_week} tone="bg-lime-50 text-lime-700 border-lime-200" />
          </div>
          <div className="mt-4 flex justify-end">
            <Link to="/processes/timeline" className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="link-timeline">
              Ver timeline <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </SectionCard>

        <SectionCard testid="section-responsible" title="Responsáveis" subtitle="Processos ativos por usuário">
          {topResponsible.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum responsável atribuído.</p>
          ) : (
            <ul className="space-y-2">
              {topResponsible.map((r) => (
                <li key={r.name} data-testid={`resp-${r.name}`} className="flex items-center gap-2 text-sm">
                  <div className="h-7 w-7 rounded-full grid place-items-center bg-primary/10 text-primary border border-primary/30 text-[11px] font-semibold uppercase">
                    {r.name.slice(0, 2)}
                  </div>
                  <span className="flex-1 truncate">{r.name}</span>
                  <Badge variant="outline" className="text-[10px]">{r.active}</Badge>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* Row 3: Latest processes + Latest history */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SectionCard
          testid="section-latest-processes"
          title="Últimos processos atualizados"
          action={
            <Link to="/processes/list" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
              Ver todos <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          {latestProcesses.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum processo cadastrado ainda.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="latest-processes-list">
              {latestProcesses.map((p) => (
                <li key={p.id} data-testid={`latest-process-${p.id}`} className="py-2.5 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/processes/${p.id}`} className="text-sm font-mono font-semibold text-foreground hover:text-primary">
                        {p.process_number}
                      </Link>
                      {p.workflow_stage && (
                        <Badge variant="outline" className="text-[10px] uppercase">
                          {STAGE_LABELS[p.workflow_stage] || p.workflow_stage}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {p.client_name || "—"} · {p.service_name || "—"}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex-1"><ProgressBar value={p.progress_pct} /></div>
                      <span className="text-[10px] tabular-nums text-muted-foreground w-9 text-right">{p.progress_pct}%</span>
                    </div>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">{fmtRelative(p.last_update)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          testid="section-latest-history"
          title="Últimos eventos do histórico"
          action={
            <Link to="/processes/history" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
              Ver histórico <ArrowRight className="h-3 w-3" />
            </Link>
          }
        >
          <HistoryList
            items={latestHistory}
            showProcess
            emptyLabel="Nenhum evento registrado ainda."
          />
        </SectionCard>
      </div>
    </div>
  );
}

function PriorityRow({ testid, label, count, total, tone, barTone, icon: Icon }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <li data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`h-6 w-6 rounded-md border grid place-items-center ${tone}`}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
        <span className="text-sm font-medium text-foreground flex-1">{label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{count} · {pct}%</span>
      </div>
      <ProgressBar value={pct} tone={barTone} />
    </li>
  );
}

function MiniStat({ testid, icon: Icon, label, value, tone }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border p-3 text-center">
      <div className={`h-9 w-9 rounded-lg mx-auto grid place-items-center border ${tone}`}>
        <Icon className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
